import { useEffect, useRef, useState } from "react";
import type { ImApi } from "./domain/api";
import { ImApi as ImApiClient } from "./domain/api";
import type { MaterializedMessage } from "./domain/messages";
import {
  accessToken,
  beginAuthorization,
  completeAuthorization,
  hasAuthorizationCallback,
  loadTokens,
  loginWithPassword,
  logoutFromSso,
  registerWithPassword,
  type PasswordLoginInput,
  type RegistrationInput,
} from "./domain/oidc";
import { ImSocket } from "./domain/socket";
import type {
  Account,
  ConnectionStatus,
  ConversationDescription,
  ConversationSummary,
  DirectoryUser,
  PersistedMessage,
  SendDraft,
  UserProfile,
} from "./domain/types";
import { AuthScreen } from "./components/AuthScreen";
import { Avatar } from "./components/Avatar";
import { ChatPanel } from "./components/ChatPanel";
import { ContactsCenter } from "./components/ContactsCenter";
import { ConversationList } from "./components/ConversationList";
import { Icon } from "./components/Icon";
import { NewConversationDialog } from "./components/NewConversationDialog";

type Phase = "booting" | "auth" | "ready";
export default function App() {
  const [phase, setPhase] = useState<Phase>("booting");
  const [authBusy, setAuthBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [api, setApi] = useState<ImApi | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("offline");
  const [conversations, setConversations] = useState<readonly ConversationSummary[]>([]);
  const [users, setUsers] = useState<readonly DirectoryUser[]>([]);
  const [selected, setSelected] = useState<ConversationSummary | null>(null);
  const [description, setDescription] = useState<ConversationDescription | null>(null);
  const [messages, setMessages] = useState<Record<string, readonly PersistedMessage[]>>({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const apiRef = useRef<ImApi | null>(null);
  const socketRef = useRef<ImSocket | null>(null);
  const accountRef = useRef<Account | null>(null);
  const selectedRef = useRef<string | null>(null);
  const messagesRef = useRef<Record<string, readonly PersistedMessage[]>>({});
  const totalUnread = conversations.reduce((total, conversation) => total + conversation.unreadCount, 0);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    let active = true;
    async function initialize(): Promise<void> {
      try {
        if (hasAuthorizationCallback()) await completeAuthorization();
        if (loadTokens() === null) {
          if (active) setPhase("auth");
          return;
        }
        const nextApi = new ImApiClient(accessToken);
        const nextAccount = await nextApi.bootstrap();
        const [nextConversations, nextUsers] = await Promise.all([
          nextApi.conversations(),
          nextApi.searchUsers(),
        ]);
        if (!active) {
          nextApi.dispose();
          return;
        }
        apiRef.current = nextApi;
        accountRef.current = nextAccount;
        setApi(nextApi);
        setAccount(nextAccount);
        setConversations(nextConversations);
        setUsers(nextUsers);
        setPhase("ready");
        const socket = new ImSocket(
          nextApi,
          (incoming) => { addRealtimeMessages(incoming); },
          setStatus,
          showToast,
          async () => {
            const latestConversations = await nextApi.conversations();
            if (!active) return;
            setConversations((current) => mergeConversationSummaries(current, latestConversations));

            // Only materialized timelines need immediate recovery. Other
            // conversations expose authoritative unread counts and sync when opened.
            const loadedConversationIds = new Set(Object.keys(messagesRef.current));
            if (selectedRef.current !== null) loadedConversationIds.add(selectedRef.current);
            const byId = new Map(latestConversations.map((item) => [item.convId, item]));
            for (const convId of loadedConversationIds) {
              const summary = byId.get(convId);
              if (summary === undefined) continue;
              const afterSeq = highestSequence(messagesRef.current[convId] ?? []);
              if (afterSeq >= summary.currentSeq) continue;
              const recovered = await syncThrough(nextApi, convId, afterSeq, summary.currentSeq);
              if (apiRef.current !== nextApi) return;
              addRealtimeMessages(recovered);
            }
          },
        );
        socketRef.current = socket;
        await socket.connect();
      } catch (cause) {
        if (!active) return;
        setError(errorMessage(cause));
        setPhase("auth");
      }
    }
    void initialize();
    return () => {
      active = false;
      socketRef.current?.close();
      apiRef.current?.dispose();
    };
  }, []);

  useEffect(() => {
    function closeTransientUi(event: KeyboardEvent): void {
      if (event.key !== "Escape") return;
      setShowAccount(false);
      setShowCreate(false);
      setShowContacts(false);
    }
    window.addEventListener("keydown", closeTransientUi);
    return () => { window.removeEventListener("keydown", closeTransientUi); };
  }, []);

  function showToast(message: string): void {
    setToast(message);
    window.setTimeout(() => { setToast((current) => current === message ? null : current); }, 3_500);
  }

  function addRealtimeMessages(incoming: readonly PersistedMessage[]): void {
    if (incoming.length === 0) return;
    setMessages((current) => {
      const next = { ...current };
      for (const message of incoming) {
        next[message.convId] = mergeMessages(next[message.convId] ?? [], [message]);
      }
      return next;
    });
    const latest = new Map<string, PersistedMessage>();
    for (const message of incoming) {
      const current = latest.get(message.convId);
      if (current === undefined || current.seq < message.seq) latest.set(message.convId, message);
    }
    setConversations((current) => current.map((conversation) => {
      const message = latest.get(conversation.convId);
      if (message === undefined) return conversation;
      const isRead = selectedRef.current === conversation.convId ||
        message.senderId === accountRef.current?.userId;
      if (isRead) socketRef.current?.markRead(conversation.convId, message.seq);
      return {
        ...conversation,
        lastMessage: conversation.lastMessage === undefined ||
            conversation.lastMessage === null ||
            message.seq >= conversation.lastMessage.seq
          ? message
          : conversation.lastMessage,
        lastMessageSeq: Math.max(conversation.lastMessageSeq, message.seq),
        currentSeq: Math.max(conversation.currentSeq, message.seq),
        lastDeliveredSeq: Math.max(conversation.lastDeliveredSeq, message.seq),
        lastReadSeq: isRead ? Math.max(conversation.lastReadSeq, message.seq) : conversation.lastReadSeq,
        unreadCount: isRead ? 0 : Math.max(0, message.seq - conversation.lastReadSeq),
        updatedAt: message.createdAt,
      };
    }).sort(compareConversations));
  }

  async function selectConversation(conversation: ConversationSummary): Promise<void> {
    if (apiRef.current === null) return;
    selectedRef.current = conversation.convId;
    setSelected(conversation);
    setDescription(null);
    setHistoryLoading(true);
    try {
      const detailPromise = apiRef.current.conversation(conversation.convId);
      const history: PersistedMessage[] = [];
      let cursor = 0;
      let currentSeq = conversation.currentSeq;
      for (let page = 0; page < 20; page += 1) {
        const result = await apiRef.current.sync(conversation.convId, cursor);
        history.push(...result.messages);
        currentSeq = result.currentSeq;
        if (result.nextAfterSeq <= cursor || result.nextAfterSeq >= currentSeq) break;
        cursor = result.nextAfterSeq;
      }
      if (selectedRef.current !== conversation.convId) return;
      const detail = await detailPromise;
      setDescription(detail);
      setMessages((current) => ({ ...current, [conversation.convId]: mergeMessages([], history) }));
      if (currentSeq > 0) socketRef.current?.markRead(conversation.convId, currentSeq);
      setConversations((current) => current.map((item) => item.convId === conversation.convId
        ? { ...item, unreadCount: 0, lastReadSeq: Math.max(item.lastReadSeq, currentSeq) }
        : item));
    } catch (cause) {
      showToast(errorMessage(cause));
    } finally {
      if (selectedRef.current === conversation.convId) setHistoryLoading(false);
    }
  }

  async function sendMessage(draft: SendDraft): Promise<void> {
    if (selected === null || apiRef.current === null || socketRef.current === null) return;
    try {
      const ack = await socketRef.current.send(selected.convId, draft);
      const current = messages[selected.convId] ?? [];
      const afterSeq = current.reduce((max, message) => Math.max(max, message.seq), 0);
      if (afterSeq < ack.seq) {
        const synced = await apiRef.current.sync(selected.convId, afterSeq);
        addRealtimeMessages(synced.messages);
      }
      socketRef.current.markRead(selected.convId, ack.seq);
    } catch (cause) {
      showToast(errorMessage(cause));
      throw cause;
    }
  }

  async function createConversation(input: {
    readonly type: "p2p" | "group" | "broadcast";
    readonly title?: string;
    readonly users: readonly DirectoryUser[];
  }): Promise<void> {
    if (apiRef.current === null || account === null) return;
    setCreating(true);
    try {
      const result = await apiRef.current.createConversation({
        type: input.type,
        ...(input.title === undefined ? {} : { title: input.title }),
        members: [
          { userId: account.userId, role: "owner", pool: account.homePool },
          ...input.users.map((user) => ({ userId: user.userId, role: "member" as const, pool: user.homePool })),
        ],
      });
      const title = input.type === "p2p" ? input.users[0]?.displayName ?? "私聊" : input.title ?? "群聊";
      const optimistic: ConversationSummary = {
        convId: result.convId,
        type: result.type,
        title,
        avatarUrl: input.type === "p2p" ? input.users[0]?.avatarUrl ?? null : null,
        peerUserId: input.type === "p2p" ? input.users[0]?.userId ?? null : null,
        homePool: account.homePool,
        lastMessage: null,
        lastMessageSeq: 0,
        lastDeliveredSeq: 0,
        lastReadSeq: 0,
        unreadCount: 0,
        pendingMentionCount: 0,
        pinned: false,
        muted: false,
        markedUnread: false,
        draft: null,
        clearedThroughSeq: 0,
        currentSeq: 0,
        memberVersion: 1,
        updatedAt: Date.now(),
      };
      setConversations((current) => [optimistic, ...current.filter((item) => item.convId !== optimistic.convId)]);
      setShowCreate(false);
      await selectConversation(optimistic);
    } catch (cause) {
      showToast(errorMessage(cause));
    } finally {
      setCreating(false);
    }
  }

  async function startP2pConversation(user: DirectoryUser): Promise<void> {
    await createConversation({ type: "p2p", users: [user] });
    setShowContacts(false);
  }

  async function updatePreference(
    conversation: ConversationSummary,
    patch: Parameters<ImApi["updateConversationPreference"]>[1],
  ): Promise<void> {
    if (apiRef.current === null) return;
    try {
      await apiRef.current.updateConversationPreference(conversation.convId, patch);
      if (patch.hidden === true) {
        setConversations((current) => current.filter((item) => item.convId !== conversation.convId));
        selectedRef.current = null;
        setSelected(null);
        setDescription(null);
        return;
      }
      if (patch.clearHistory === true) {
        setMessages((current) => ({ ...current, [conversation.convId]: [] }));
      }
      setConversations((current) => current.map((item) => item.convId === conversation.convId
        ? {
            ...item,
            ...(patch.pinned === undefined ? {} : { pinned: patch.pinned }),
            ...(patch.muted === undefined ? {} : { muted: patch.muted }),
            ...(patch.markedUnread === undefined ? {} : {
              markedUnread: patch.markedUnread,
              unreadCount: patch.markedUnread ? Math.max(1, item.unreadCount) : item.unreadCount,
            }),
            ...(patch.draft === undefined ? {} : { draft: patch.draft }),
            ...(patch.clearHistory === true ? { clearedThroughSeq: item.currentSeq } : {}),
          }
        : item).sort(compareConversations));
      setSelected((current) => current?.convId === conversation.convId
        ? {
            ...current,
            ...(patch.pinned === undefined ? {} : { pinned: patch.pinned }),
            ...(patch.muted === undefined ? {} : { muted: patch.muted }),
          }
        : current);
      showToast("会话设置已更新");
    } catch (cause) {
      showToast(errorMessage(cause));
    }
  }

  function applyProfile(profile: UserProfile): void {
    setAccount((current) => current === null ? null : {
      ...current,
      displayName: profile.displayName,
      avatarUrl: profile.avatarUrl,
    });
    setUsers((current) => current.map((user) => user.userId === profile.userId
      ? { ...user, displayName: profile.displayName, avatarUrl: profile.avatarUrl }
      : user));
  }

  function editMessage(message: MaterializedMessage): void {
    const value = window.prompt("编辑消息", message.payload);
    if (value === null || value.trim().length === 0) return;
    void sendMessage({ contentType: "edit", payload: value.trim(), targetMessageId: message.messageId })
      .catch(() => undefined);
  }

  function retractMessage(message: MaterializedMessage): void {
    if (!window.confirm("确认撤回这条消息？")) return;
    void sendMessage({ contentType: "retract", payload: "", targetMessageId: message.messageId })
      .catch(() => undefined);
  }

  async function logout(): Promise<void> {
    setShowAccount(false);
    socketRef.current?.close();
    await apiRef.current?.revokeSession().catch(() => undefined);
    await logoutFromSso();
    apiRef.current?.dispose();
    apiRef.current = null;
    setApi(null);
    setAccount(null);
    accountRef.current = null;
    setPhase("auth");
  }

  async function authenticateWithPassword(
    action: () => Promise<unknown>,
  ): Promise<void> {
    setAuthBusy(true);
    setError(null);
    try {
      await action();
      setPhase("booting");
      window.location.reload();
    } catch (cause) {
      setError(errorMessage(cause));
      setAuthBusy(false);
    }
  }

  if (phase === "booting") {
    return <main className="boot-screen"><div className="brand-loader"><Icon name="chat" /></div><p>正在连接 Sonox IM</p></main>;
  }
  if (phase === "auth" || account === null || api === null) {
    return <AuthScreen busy={authBusy} error={error}
      onPasswordLogin={(input: PasswordLoginInput) => {
        void authenticateWithPassword(() => loginWithPassword(input));
      }}
      onRegister={(input: RegistrationInput) => {
        void authenticateWithPassword(() => registerWithPassword(input));
      }}
      onFeishuLogin={() => {
        setAuthBusy(true);
        setError(null);
        void beginAuthorization().catch((cause: unknown) => {
          setError(errorMessage(cause));
          setAuthBusy(false);
        });
      }} />;
  }

  return (
    <main className="app-shell">
      <nav className="nav-rail" aria-label="主导航">
        <button className="account-avatar" type="button" aria-label="账号与连接状态"
          aria-expanded={showAccount} aria-haspopup="dialog"
          onClick={() => { setShowAccount((value) => !value); }}>
          <Avatar name={account.displayName} url={account.avatarUrl} size={42} status={status === "online" ? "online" : "offline"} />
        </button>
        <div className="rail-main"><button className="active rail-chat-button" type="button"
          aria-label="聊天" aria-current="page"><Icon name="chat" />
          {totalUnread > 0 ? <b className="rail-unread-badge">{totalUnread > 99 ? "99+" : totalUnread}</b> : null}
        </button>
          <button type="button" aria-label="联系人" onClick={() => { setShowContacts(true); }}><Icon name="contacts" /></button></div>
        <div className="rail-bottom"><button type="button" aria-label="新建会话" onClick={() => { setShowCreate(true); }}><Icon name="plus" /></button>
          <button type="button" aria-label="退出登录" onClick={() => void logout()}><Icon name="logout" /></button></div>
        {showAccount ? <section className="account-popover" role="dialog" aria-label="账号信息"><Avatar name={account.displayName} url={account.avatarUrl} size={56} />
          <strong>{account.displayName}</strong><span>{account.email ?? account.userId}</span>
          <dl><div><dt>连接状态</dt><dd className={status}>{statusLabel(status)}</dd></div>
            <div><dt>Home Pool</dt><dd>{account.homePool}</dd></div></dl>
          <button type="button" onClick={() => void logout()}><Icon name="logout" />退出登录</button></section> : null}
      </nav>
      <ConversationList conversations={conversations} selectedId={selected?.convId ?? null}
        onSelect={(conversation) => void selectConversation(conversation)} onCreate={() => { setShowCreate(true); }} />
      <ChatPanel account={account} api={api} conversation={selected} description={description}
        messages={selected === null ? [] : messages[selected.convId] ?? []} loading={historyLoading}
        connectionStatus={status} onBack={() => { selectedRef.current = null; setSelected(null); }}
        onSend={sendMessage} onError={showToast} onEdit={editMessage} onRetract={retractMessage}
        onPreference={(conversation, patch) => { void updatePreference(conversation, patch); }} />
      {showCreate ? <NewConversationDialog account={account} users={users} busy={creating}
        onClose={() => { setShowCreate(false); }} onCreate={(input) => void createConversation(input)} /> : null}
      {showContacts ? <ContactsCenter account={account} api={api} users={users}
        onClose={() => { setShowContacts(false); }} onStartChat={startP2pConversation}
        onProfileChanged={applyProfile} onError={showToast} /> : null}
      {toast === null ? null : <div className="toast" role="status">{toast}</div>}
    </main>
  );
}

function mergeMessages(
  current: readonly PersistedMessage[],
  incoming: readonly PersistedMessage[],
): readonly PersistedMessage[] {
  const map = new Map(current.map((message) => [message.messageId, message]));
  for (const message of incoming) map.set(message.messageId, message);
  return [...map.values()].sort((left, right) => left.seq - right.seq);
}

function mergeConversationSummaries(
  current: readonly ConversationSummary[],
  authoritative: readonly ConversationSummary[],
): readonly ConversationSummary[] {
  const merged = new Map(current.map((conversation) => [conversation.convId, conversation]));
  for (const conversation of authoritative) {
    const local = merged.get(conversation.convId);
    if (local === undefined) {
      merged.set(conversation.convId, conversation);
      continue;
    }
    if (local.currentSeq > conversation.currentSeq) {
      merged.set(conversation.convId, {
        ...conversation,
        lastMessage: local.lastMessage,
        lastMessageSeq: local.lastMessageSeq,
        lastDeliveredSeq: local.lastDeliveredSeq,
        lastReadSeq: local.lastReadSeq,
        unreadCount: local.unreadCount,
        currentSeq: local.currentSeq,
        updatedAt: local.updatedAt,
      });
      continue;
    }
    merged.set(conversation.convId, {
      ...conversation,
      // During rolling deployment or projection lag the HTTP response may not
      // carry content yet. Preserve an equally recent WebSocket message.
      lastMessage: conversation.lastMessage ?? (
        local.lastMessage?.seq === conversation.lastMessageSeq ? local.lastMessage : null
      ),
    });
  }
  return [...merged.values()].sort(compareConversations);
}

function highestSequence(messages: readonly PersistedMessage[]): number {
  return messages.reduce((highest, message) => Math.max(highest, message.seq), 0);
}

/** Reads every missing page for one already-materialized timeline after reconnect. */
async function syncThrough(
  api: ImApi,
  convId: string,
  afterSeq: number,
  targetSeq: number,
): Promise<readonly PersistedMessage[]> {
  const recovered: PersistedMessage[] = [];
  let cursor = afterSeq;
  for (let page = 0; page < 20 && cursor < targetSeq; page += 1) {
    const result = await api.sync(convId, cursor);
    recovered.push(...result.messages);
    if (result.nextAfterSeq <= cursor) break;
    cursor = result.nextAfterSeq;
  }
  return recovered;
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : "发生未知错误";
}

function statusLabel(status: ConnectionStatus): string {
  if (status === "online") return "在线";
  if (status === "connecting") return "连接中";
  if (status === "reconnecting") return "重连中";
  return "离线";
}

function compareConversations(left: ConversationSummary, right: ConversationSummary): number {
  if (left.pinned !== right.pinned) return left.pinned ? -1 : 1;
  return right.updatedAt - left.updatedAt;
}
